import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    LinearProgress,
    Alert,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    CloudUpload,
    Refresh,
    Delete,
    CheckCircle,
    Pending,
    Error as ErrorIcon,
    ExpandMore,
    Description,
    Mic,
    VideoLibrary,
    RestartAlt,
    Schedule,
    FiberManualRecord,
    OpenInNew,
    AutoFixHigh,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:7071';

const AdminOperations = ({ companyId: propCompanyId, companyName: propCompanyName }) => {
    const navigate = useNavigate();
    const [companyInfo, setCompanyInfo] = useState({ companyId: '', companyName: '' });
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState({});
    const [, setUploadProgress] = useState({});
    const [processingStatus, setProcessingStatus] = useState({});
    const [clearHistoryDialogOpen, setClearHistoryDialogOpen] = useState(false);

    // Separate file state for each upload type
    const [selectedFiles, setSelectedFiles] = useState({
        annualReport: null,
        concallTranscript: null,
        video: null
    });

    const [formData, setFormData] = useState({
        annualReport: {
            fiscalYear: new Date().getFullYear(),
            periodEndDate: ''
        },
        concallTranscript: {
            fiscalYear: new Date().getFullYear(),
            fiscalQuarter: '',
            callDate: ''
        }
    });

    const [filters, setFilters] = useState({
        docType: '',
        fiscalYear: '',
        status: ''
    });

    // Polling state
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Terminal statuses — no more progress expected
    const TERMINAL_STATUSES = ['completed', 'failed'];
    const NON_TERMINAL_STATUSES = ['pending', 'queued', 'processing', 'uploading'];

    // Use company context from props or fallback to mock data
    useEffect(() => {
        if (propCompanyId && propCompanyName) {
            setCompanyInfo({
                companyId: propCompanyId,
                companyName: propCompanyName
            });
        } else {
            // Fallback for standalone page
            setCompanyInfo({
                companyId: 'RATNAMANI',
                companyName: 'Ratnamani Metals & Tubes Ltd'
            });
        }
    }, [propCompanyId, propCompanyName]);

    // Fetch documents when company changes
    useEffect(() => {
        if (companyInfo.companyId) {
            fetchDocuments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyInfo.companyId]);

    // Smart polling: start when non-terminal docs exist, stop otherwise
    useEffect(() => {
        const hasActiveJobs = documents.some(
            d => NON_TERMINAL_STATUSES.includes(d.status || d.ingestionStatus)
        );

        if (hasActiveJobs && !pollingIntervalRef.current) {
            setIsPolling(true);
            pollingIntervalRef.current = setInterval(() => {
                fetchDocuments(true);  // silent = true (no loading spinner)
            }, 4000);
        } else if (!hasActiveJobs && pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setIsPolling(false);
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents]);

    const fetchDocuments = async (silent = false) => {
        if (!companyInfo.companyId) return;

        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const response = await axios.get(`${API_BASE}/api/documents`, {
                params: { companyId: companyInfo.companyId },
                signal: controller.signal
            });

            const docs = response.data?.data || response.data || [];
            setDocuments(docs);
        } catch (error) {
            if (axios.isCancel(error) || error.name === 'CanceledError') return;
            console.error('Failed to fetch documents:', error);
            if (!silent) setDocuments([]);
        }
    };

    const handleFileSelect = (docType, event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Please select a PDF, DOCX, or TXT file');
                return;
            }
            if (file.size > 50 * 1024 * 1024) {
                toast.error('File size must be less than 50MB');
                return;
            }
            setSelectedFiles(prev => ({ ...prev, [docType]: file }));
        }
    };

    const handleUpload = async (docType) => {
        const file = selectedFiles[docType];
        if (!file) {
            toast.error('Please select a file to upload');
            return;
        }

        // Validate required fields
        if (!formData[docType].fiscalYear) {
            toast.error('Please enter the Financial Year');
            return;
        }

        if (docType === 'annualReport' && !formData[docType].periodEndDate) {
            toast.error('Please select the Period End Date');
            return;
        }

        const uploadId = `${docType}-${Date.now()}`;
        setLoading(prev => ({ ...prev, [docType]: true }));
        setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Uploading...', progress: 0 } }));

        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('companyId', companyInfo.companyId);
        uploadData.append('companyName', companyInfo.companyName);
        uploadData.append('fiscalYear', formData[docType].fiscalYear);

        // Map docType to Azure Function's documentType format
        const documentTypeMap = {
            'annualReport': 'annual_report',
            'concallTranscript': 'earnings_call',
            'video': 'video'
        };
        uploadData.append('documentType', documentTypeMap[docType] || 'annual_report');

        if (docType === 'annualReport' && formData[docType].periodEndDate) {
            uploadData.append('reportDate', formData[docType].periodEndDate);
        }
        if (docType === 'concallTranscript' && formData[docType].callDate) {
            uploadData.append('reportDate', formData[docType].callDate);
        }
        if (docType === 'concallTranscript' && formData[docType].fiscalQuarter) {
            uploadData.append('fiscalQuarter', formData[docType].fiscalQuarter);
        }

        try {
            setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Uploading file...', progress: 10 } }));

            const response = await axios.post(`${API_BASE}/api/document-upload`, uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(prev => ({ ...prev, [uploadId]: percentCompleted }));
                    setProcessingStatus(prev => ({
                        ...prev,
                        [uploadId]: { stage: 'Uploading file...', progress: percentCompleted / 2 }
                    }));
                }
            });

            // Check response for job-based processing vs inline
            const responseData = response.data?.data || response.data;
            const jobId = responseData?.jobId;

            if (jobId && responseData?.status === 'queued') {
                // Async processing - track job
                setProcessingStatus(prev => ({
                    ...prev,
                    [uploadId]: {
                        stage: 'Queued for processing...',
                        progress: 50,
                        jobId
                    }
                }));

                toast.success(`Document uploaded! Job ID: ${jobId}. Processing will continue in background.`);
                setSelectedFiles(prev => ({ ...prev, [docType]: null }));

                // Fetch documents to show new job
                fetchDocuments();

            } else {
                // Inline processing (sync response)
                setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Extracting text...', progress: 60 } }));
                await new Promise(resolve => setTimeout(resolve, 500));

                setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Chunking text...', progress: 80 } }));
                await new Promise(resolve => setTimeout(resolve, 500));

                setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Generating embeddings...', progress: 90 } }));
                await new Promise(resolve => setTimeout(resolve, 500));

                setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Completed!', progress: 100 } }));

                toast.success(`Document uploaded successfully! ${responseData?.chunksCreated || 0} chunks created.`);
                setSelectedFiles(prev => ({ ...prev, [docType]: null }));
                fetchDocuments();
            }

            setUploadProgress(prev => ({ ...prev, [uploadId]: 0 }));

            // Clear status after 3 seconds
            setTimeout(() => {
                setProcessingStatus(prev => {
                    const newState = { ...prev };
                    delete newState[uploadId];
                    return newState;
                });
            }, 3000);
        } catch (error) {
            console.error('Upload failed:', error);
            setProcessingStatus(prev => ({ ...prev, [uploadId]: { stage: 'Failed', progress: 0, error: true } }));
            toast.error(error.response?.data?.error?.message || error.response?.data?.message || 'Upload failed');

            setTimeout(() => {
                setProcessingStatus(prev => {
                    const newState = { ...prev };
                    delete newState[uploadId];
                    return newState;
                });
            }, 5000);
        } finally {
            setLoading(prev => ({ ...prev, [docType]: false }));
        }
    };

    const handleReingest = async (documentId) => {
        try {
            setLoading(prev => ({ ...prev, [`reingest_${documentId}`]: true }));
            await axios.post(`${API_BASE}/api/documents/${documentId}/reingest`);
            toast.success('Re-ingestion queued. Processing will start shortly.');
            fetchDocuments();
        } catch (error) {
            console.error('Reingest failed:', error);
            const msg = error.response?.data?.message || 'Re-ingestion failed. Please try again.';
            toast.error(msg);
        } finally {
            setLoading(prev => ({ ...prev, [`reingest_${documentId}`]: false }));
        }
    };

    const handleClearUploadHistory = async () => {
        if (!companyInfo.companyId) return;

        try {
            setLoading(prev => ({ ...prev, clearHistory: true }));
            const response = await axios.delete(`${API_BASE}/api/documents`, {
                params: { companyId: companyInfo.companyId }
            });
            const deletedCount = response.data?.data?.deletedCount ?? 0;

            setClearHistoryDialogOpen(false);
            setDocuments([]);
            toast.success(
                deletedCount === 1
                    ? 'Cleared 1 upload history record.'
                    : `Cleared ${deletedCount} upload history records.`
            );
            await fetchDocuments();
        } catch (error) {
            console.error('Failed to clear upload history:', error);
            const msg = error.response?.data?.message || 'Failed to clear upload history. Please try again.';
            toast.error(msg);
        } finally {
            setLoading(prev => ({ ...prev, clearHistory: false }));
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle color="success" />;
            case 'processing':
                return <Schedule color="warning" />;
            case 'queued':
            case 'pending':
                return <Pending color="info" />;
            case 'failed':
                return <ErrorIcon color="error" />;
            default:
                return <Pending color="action" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'processing':
                return 'warning';
            case 'queued':
            case 'pending':
                return 'info';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const filteredDocuments = documents.filter(doc => {
        const docType = doc.docType || doc.document_type || '';
        const fy = doc.fiscal_year || doc.fiscalYear;
        const status = doc.ingestionStatus || doc.status || '';

        if (filters.docType && docType !== filters.docType) return false;
        if (filters.fiscalYear && String(fy) !== String(filters.fiscalYear)) return false;
        if (filters.status && status !== filters.status) return false;
        return true;
    });

    return (
        <Box sx={{ p: 3 }}>
            {!propCompanyId && (
                <Button
                    variant="text"
                    onClick={() => navigate('/home')}
                    sx={{ mb: 1, color: 'var(--text-2)', textTransform: 'none', fontWeight: 600 }}
                >
                    ← Home
                </Button>
            )}
            {/* Header - Simplified for embedded view */}
            {!propCompanyId && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h4" gutterBottom>
                        Admin Operations
                    </Typography>
                    <Typography variant="h6">
                        Company: {companyInfo.companyName} ({companyInfo.companyId})
                    </Typography>
                </Paper>
            )}

            {/* Upload Sections - Accordion Format */}
            <Box sx={{ mb: 3 }}>
                {/* Annual Reports Upload */}
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description color="primary" />
                            <Typography variant="h6">Annual Reports Upload</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                            {/* ── NEW: page-selection ingest banner ── */}
                            <Alert
                                severity="info"
                                icon={<AutoFixHigh />}
                                sx={{ mb: 3 }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        endIcon={<OpenInNew />}
                                        onClick={() => navigate('/admin/ingest')}
                                    >
                                        Select Pages
                                    </Button>
                                }
                            >
                                <strong>Recommended:</strong> Use the new <em>Ingest Annual Report</em> workflow to select exactly which pages to process — faster, cheaper, and produces cleaner chunks.
                            </Alert>

                            <TextField
                                fullWidth
                                required
                                label="Financial Year"
                                type="number"
                                value={formData.annualReport.fiscalYear}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    annualReport: { ...formData.annualReport, fiscalYear: parseInt(e.target.value) }
                                })}
                                helperText="Required field"
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                required
                                label="Period End Date"
                                type="date"
                                value={formData.annualReport.periodEndDate}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    annualReport: { ...formData.annualReport, periodEndDate: e.target.value }
                                })}
                                InputLabelProps={{ shrink: true }}
                                helperText="Required field"
                                sx={{ mb: 2 }}
                            />

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUpload />}
                                sx={{ mb: 2 }}
                            >
                                Select PDF File
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.txt"
                                    hidden
                                    onChange={(e) => handleFileSelect('annualReport', e)}
                                />
                            </Button>

                            {selectedFiles.annualReport && (
                                <Box sx={{ mb: 2 }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Selected: {selectedFiles.annualReport.name} ({(selectedFiles.annualReport.size / 1024 / 1024).toFixed(2)} MB)
                                    </Alert>

                                    {Object.entries(processingStatus).filter(([id]) => id.startsWith('annualReport-')).map(([id, status]) => (
                                        <Box key={id} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2">{status.stage}</Typography>
                                                <Typography variant="body2">{Math.round(status.progress)}%</Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={status.progress}
                                                color={status.error ? 'error' : 'primary'}
                                            />
                                        </Box>
                                    ))}

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleUpload('annualReport')}
                                        disabled={loading.annualReport}
                                        fullWidth
                                        startIcon={<CloudUpload />}
                                    >
                                        {loading.annualReport ? 'Uploading...' : 'Upload Annual Report'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Concall Transcripts Upload */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Mic color="secondary" />
                            <Typography variant="h6">Concall Transcripts Upload</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                            <TextField
                                fullWidth
                                required
                                label="Financial Year"
                                type="number"
                                value={formData.concallTranscript.fiscalYear}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    concallTranscript: { ...formData.concallTranscript, fiscalYear: parseInt(e.target.value) }
                                })}
                                helperText="Required field"
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Fiscal Quarter (e.g., Q1, Q2)"
                                value={formData.concallTranscript.fiscalQuarter}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    concallTranscript: { ...formData.concallTranscript, fiscalQuarter: e.target.value }
                                })}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Call Date"
                                type="date"
                                value={formData.concallTranscript.callDate}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    concallTranscript: { ...formData.concallTranscript, callDate: e.target.value }
                                })}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 2 }}
                            />

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUpload />}
                                sx={{ mb: 2 }}
                            >
                                Select File
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.txt"
                                    hidden
                                    onChange={(e) => handleFileSelect('concallTranscript', e)}
                                />
                            </Button>

                            {selectedFiles.concallTranscript && (
                                <Box sx={{ mb: 2 }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Selected: {selectedFiles.concallTranscript.name} ({(selectedFiles.concallTranscript.size / 1024 / 1024).toFixed(2)} MB)
                                    </Alert>

                                    {Object.entries(processingStatus).filter(([id]) => id.startsWith('concallTranscript-')).map(([id, status]) => (
                                        <Box key={id} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2">{status.stage}</Typography>
                                                <Typography variant="body2">{Math.round(status.progress)}%</Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={status.progress}
                                                color={status.error ? 'error' : 'primary'}
                                            />
                                        </Box>
                                    ))}

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => handleUpload('concallTranscript')}
                                        disabled={loading.concallTranscript}
                                        fullWidth
                                        startIcon={<CloudUpload />}
                                    >
                                        {loading.concallTranscript ? 'Uploading...' : 'Upload Transcript'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Future: Video Uploads */}
                <Accordion disabled>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VideoLibrary color="disabled" />
                            <Typography variant="h6" color="text.disabled">Video Uploads (Coming Soon)</Typography>
                        </Box>
                    </AccordionSummary>
                </Accordion>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Filters</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Document Type</InputLabel>
                                <Select
                                    value={filters.docType}
                                    onChange={(e) => setFilters({ ...filters, docType: e.target.value })}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="annual_report">Annual Report</MenuItem>
                                    <MenuItem value="earnings_call">Concall Transcript</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Fiscal Year"
                                type="number"
                                value={filters.fiscalYear}
                                onChange={(e) => setFilters({ ...filters, fiscalYear: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="queued">Queued</MenuItem>
                                    <MenuItem value="processing">Processing</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="failed">Failed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={fetchDocuments}
                                fullWidth
                                sx={{ height: '56px' }}
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Documents Table */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">
                                Uploaded Documents ({filteredDocuments.length})
                            </Typography>
                            {isPolling && (
                                <Chip
                                    icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
                                    label="LIVE"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{
                                        animation: 'pulse 1.5s infinite',
                                        '@keyframes pulse': {
                                            '0%': { opacity: 1 },
                                            '50%': { opacity: 0.5 },
                                            '100%': { opacity: 1 },
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Delete />}
                            onClick={() => setClearHistoryDialogOpen(true)}
                            disabled={documents.length === 0 || !!loading.clearHistory}
                        >
                            {loading.clearHistory ? 'Clearing...' : 'Clear Upload History'}
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Fiscal Year</TableCell>
                                    <TableCell>Quarter</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Chunks</TableCell>
                                    <TableCell>Uploaded</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDocuments.map((doc) => {
                                    const status = doc.ingestionStatus || doc.status || 'pending';
                                    const chunksDone = doc.chunks_created || 0;
                                    const chunksTotal = doc.chunks_total || 0;
                                    const chunkDisplay = status === 'completed'
                                        ? (doc.chunk_summary?.total || chunksDone || '-')
                                        : chunksTotal > 0 ? `${chunksDone}/${chunksTotal}` : '-';
                                    const docType = doc.docType || doc.document_type || '';
                                    const docTypeLabel = docType === 'annual_report' ? 'Annual Report'
                                        : docType === 'earnings_call' ? 'Concall'
                                            : docType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-';

                                    return (
                                        <TableRow key={doc._id || doc.job_id}>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                                    {doc.title || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={docTypeLabel}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{doc.fiscal_year || doc.fiscalYear || '-'}</TableCell>
                                            <TableCell>{doc.fiscalQuarter || '-'}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {getStatusIcon(status)}
                                                        <Chip
                                                            label={status.toUpperCase()}
                                                            size="small"
                                                            color={getStatusColor(status)}
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Box>
                                                    {doc.message && !TERMINAL_STATUSES.includes(status) && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                            {doc.message}
                                                        </Typography>
                                                    )}
                                                    {status === 'failed' && doc.error_message && (
                                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                            {doc.error_message}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{chunkDisplay}</TableCell>
                                            <TableCell>
                                                {doc.createdAt || doc.created_at
                                                    ? new Date(doc.createdAt || doc.created_at).toLocaleDateString()
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Re-ingest">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleReingest(doc._id || doc.job_id)}
                                                        disabled={status === 'processing' || status === 'queued' || !!loading[`reingest_${doc._id || doc.job_id}`]}
                                                    >
                                                        <RestartAlt />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filteredDocuments.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                No documents found matching the current filters
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={clearHistoryDialogOpen}
                onClose={() => {
                    if (!loading.clearHistory) setClearHistoryDialogOpen(false);
                }}
                aria-labelledby="clear-upload-history-title"
            >
                <DialogTitle id="clear-upload-history-title">
                    Clear upload history?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove all upload history for {companyInfo.companyName || companyInfo.companyId},
                        including Processing, Completed, and Failed records. Source PDF files and ingested
                        document data will not be deleted. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setClearHistoryDialogOpen(false)}
                        disabled={!!loading.clearHistory}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleClearUploadHistory}
                        color="error"
                        variant="contained"
                        disabled={!!loading.clearHistory}
                    >
                        {loading.clearHistory ? 'Clearing...' : 'Clear History'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminOperations;
